import React, { useState, useEffect } from 'react';
import {
    IonFab,
    IonFabButton,
    IonIcon,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonText,
    IonBadge
} from '@ionic/react';
import { bookOutline, closeOutline, informationCircleOutline, helpCircleOutline } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import './GlobalHelp.css';

const GlobalHelp = ({ inline = false }) => {
    const [showModal, setShowModal] = useState(false);
    const location = useLocation();
    const [helpContent, setHelpContent] = useState(null);

    const helpMapping = {
        '/page/view': {
            title: 'Exploring Polls',
            description: 'The main hub of Peopoll where you can find and participate in community discussions.',
            sections: [
                { title: '🔍 Search & Filter', detail: 'Found at the top of the page. Use the search bar to find specific topics. Results update instantly as you type.' },
                { title: '🗳️ Poll Cards', detail: 'Each card represents a question. It shows the main question in bold, the creation date, and the total number of votes cast so far.' },
                { title: '🏷️ Status Badges', detail: 'Look for "Voted" badges indicating polls you completed, and "Expired" badges for polls that are no longer accepting votes.' },
                { title: '⏱️ Time Remaining', detail: 'A small clock icon and timer show how much time is left to cast your vote before the poll closes.' },
                { title: '➕ Start a Poll', detail: 'The floating orange button at the bottom right is your shortcut to the "Create Poll" page.' },
                { title: '📱 Sidebar Filters', detail: 'Tap the menu icon to switch between "General Polls" and "My ORG Polls" (for organization members).' }
            ]
        },
        '/page/add': {
            title: 'Creating Your Poll',
            description: 'Follow these steps to craft a perfect poll and get accurate feedback.',
            sections: [
                { title: '📝 Your Question', detail: 'Type the main question in the top box. Keep it clear and concise for the best engagement.' },
                { title: '✨ AI Enhancement', detail: 'Stuck on wording? Tap the "Enhance with AI" button. Our assistant will rewrite your question to be more professional and engaging.' },
                { title: '🤖 Smart AI Assistant', detail: 'Super Admins and Org Admins can use the AI Assistant to generate complete polls from a simple topic or prompt.' },
                { title: '🌐 Search Grounding', detail: 'When using AI, toggle this to allow the AI to research real-time facts on the web before creating your poll.' },
                { title: '🎨 Theme Color', detail: 'Tap the color circles to set a brand theme for your poll. This color will be used for the bars and accents on the voting page.' },
                { title: '✅ Answer Options', detail: 'Add up to 5 choices. Tap the "Add Answer" button to create a new slot, or tap an existing one to delete it.' }
            ]
        },
        '/page/view/': {
            title: 'Voting & Results',
            description: 'Dive deep into the data and make your voice heard.',
            sections: [
                { title: '📊 Casting a Vote', detail: 'Tap directly on any answer bar. Once you vote, the page will instantly refresh to show the live results.' },
                { title: '📈 Real-time Analytics', detail: 'The bars expand based on the percentage of total votes. You can see exactly which opinion is leading in real-time.' },
                { title: '🔗 Copy Link', detail: 'Tap the copy icon near the poll URL to grab a direct link to this poll for sharing.' },
                { title: '📱 Social Sharing', detail: 'Instant share to WhatsApp, X, and Facebook. Super Admin polls are automatically public.' },
                { title: '🗓️ Metadata', detail: 'Scroll to the bottom to see who created the poll and the exact timestamp of its creation.' }
            ]
        },
        '/admin': {
            title: 'Admin Dashboard',
            description: 'Overview of the entire system performance and quick access to management tools.',
            sections: [
                { title: '📊 Overview Cards', detail: 'Top stats showing Total Users, Active Polls, Verified Users, and Pending Reports. Updates in real-time.' },
                { title: '⚡ Quick Actions', detail: 'Four main buttons to jump to key tasks: "View Reports" (Analytics), "Manage Users", "Poll Analytics", and "Moderation".' },
                { title: '📈 Activity Charts', detail: 'Visual graphs showing User Growth and Poll Creation trends over the last 6 months.' },
                { title: '🔔 Recent Activity', detail: 'A live feed at the bottom listing the latest user registrations and poll creations.' }
            ]
        },
        '/admin/analytics': {
            title: 'Poll Analytics',
            description: 'Deep dive into voting trends and platform usage statistics.',
            sections: [
                { title: '🔢 Key Metrics', detail: 'Cards display Active Polls, Total Votes Cast, and Engagement Rate (votes/view).' },
                { title: '🥧 Distribution Charts', detail: 'Pie charts showing the breakdown of Poll Categories and Votes by Region.' },
                { title: '📥 Export Data', detail: 'Use the "Export CSV" button to download raw analytics data for external reporting.' }
            ]
        },
        '/admin/users': {
            title: 'User Management',
            description: 'Manage all registered accounts, verify identities, and handle access.',
            sections: [
                { title: '📋 User List', detail: 'Comprehensive table of all users. Shows Name, Email, Role, Status, and Registration Date.' },
                { title: '🔍 Filter & Search', detail: 'Use the search bar to find users by name/email. Filter by "Active", "Banned", or "Pending" status.' },
                { title: '✏️ Edit Profile', detail: 'Tap the Pencil icon to update details. Now features robust prefilling for Names, Phone, and Organization.' },
                { title: '⛔ Suspend/Ban', detail: 'Use the action menu to temporarily suspend or permanently ban users violating policies.' },
                { title: '➕ Add User', detail: 'Use the floating "+" button to manually register a new administrator or special user.' }
            ]
        },
        '/admin/polls': {
            title: 'Poll Management',
            description: 'Oversee all content created on the platform.',
            sections: [
                { title: '🗂️ Poll Repository', detail: 'Browse every poll ever created. Sort by Date, Popularity, or Reports.' },
                { title: '👁️ Visibility Control', detail: 'Manage "Public" and "Organization" poll visibility. Super Admins see all public polls here.' },
                { title: '🗑️ Delete Content', detail: 'Remove inappropriate or outdated polls permanently from the system.' },
                { title: '⭐ Featured Polls', detail: 'Mark high-quality polls as "Featured" to promote them on the home page.' }
            ]
        },
        '/admin/moderation': {
            title: 'Content Moderation',
            description: 'Review reported content and take action to keep the community safe.',
            sections: [
                { title: '🛡️ Report Cards', detail: 'Each card represents a reported item (User or Poll). It shows the Reason, Reporter, and Content snippet.' },
                { title: '🚦 Quick Actions', detail: 'Use the colored buttons on the card: Green (APPROVE/Clear), Red (REJECT/Delete), Orange (INVESTIGATE).' },
                { title: '⚖️ Action Modal', detail: 'Clicking an action opens a detailed modal where you can select a specific resolution (Warn, Suspend, etc.) and add internal notes.' },
                { title: '📝 Resolution Notes', detail: 'Notes added during moderation are saved and displayed on the card for audit trails.' },
                { title: '📂 Filters', detail: 'Filter reports by "Pending", "Resolved", or "High Priority" to focus your workflow.' }
            ]
        },
        '/org-admin': {
            title: 'Organization Portal',
            description: 'Private management space for organization leaders and teams.',
            sections: [
                { title: '🏘️ Internal Polls', detail: 'Lists all polls created specifically for your organization members. These are not visible to the general public.' },
                { title: '🫂 Member Directory', detail: 'Manage the specific individuals who belong to your organization account.' },
                { title: '📊 Org Analytics', detail: 'Specialized reports focusing on how your organization members are engaging with internal content.' }
            ]
        },
        '/page/login': {
            title: 'Signing In',
            description: 'Access your personalized profile and dashboard.',
            sections: [
                { title: '💌 Email & Password', detail: 'Enter your credentials to securely access your data. We support 30-day persistent login.' },
                { title: '🔑 Password Recovery', detail: 'Forgot your password? Tap the "Reset" link to receive a secure recovery email.' },
                { title: '🆕 Need an Account?', detail: 'Tap the "Join Now" link at the bottom to transition to the registration portal.' }
            ]
        },
        '/page/register': {
            title: 'Join the Community',
            description: 'Create your Peopoll account in a few simple steps.',
            sections: [
                { title: '👤 Registration Type', detail: 'Choose "Individual" for personal use or "Organization" to manage your company or group polls.' },
                { title: '📝 Basic Info', detail: 'Enter your name, email, and choose a strong password. You will use these for all future logins.' },
                { title: '🏢 Organization Details', detail: 'If registering an organization, providing your business name and type helps us customize your dashboard.' },
                { title: '✅ Multi-Step Flow', detail: 'Use the "Next" and "Back" buttons to navigate through the registration steps. Your progress is saved locally as you go.' }
            ]
        }
    };

    useEffect(() => {
        const path = location.pathname;
        let content = helpMapping[path];

        // Handle dynamic poll view path
        if (!content && path.startsWith('/page/view/')) {
            content = helpMapping['/page/view/'];
        }

        if (!content) {
            content = {
                title: 'Quick Navigation',
                description: 'Welcome to Peopoll! Here is how to find your way around.',
                sections: [
                    { title: '🏠 Return Home', detail: 'Tap the logo or use the side menu to return to the main poll listing at any time.' },
                    { title: '📂 Admin Panels', detail: 'If you are a manager, your specific dashboard is available in the side menu for quick access.' },
                    { title: '🔒 Log Out', detail: 'Found at the bottom of the side menu. Always log out on shared devices!' },
                    { title: '🆘 Still Need Help?', detail: 'Reach out to our support team if you encounter any technical issues using the dashboard.' }
                ]
            };
        }

        setHelpContent(content);
    }, [location.pathname]);

    if (!helpContent) return null;

    // Logic to determine if we should render the floating FAB
    const hiddenPaths = ['/admin/users', '/page/add'];
    // If not inline and we are on a hidden path, render nothing
    if (!inline && hiddenPaths.includes(location.pathname)) return null;

    return (
        <>
            {inline ? (
                <IonButton onClick={() => setShowModal(true)} fill="clear" color="dark" className="help-button-inline">
                    <IonIcon icon={bookOutline} />
                </IonButton>
            ) : (
                <IonFab vertical="bottom" horizontal="end" slot="fixed" className="global-help-fab" style={{ marginBottom: '3px', marginRight: '3px' }}>
                    <IonFabButton
                        size="small"
                        onClick={() => setShowModal(true)}
                        className="help-button"
                    >
                        <IonIcon icon={bookOutline} />
                    </IonFabButton>
                </IonFab>
            )}

            <IonModal
                isOpen={showModal}
                onDidDismiss={() => setShowModal(false)}
                className="help-modal"
                initialBreakpoint={1}
                breakpoints={[0, 1]}
            >
                <IonHeader className="ion-no-border">
                    <IonToolbar className="help-toolbar">
                        <IonTitle>{helpContent.title} - Guide</IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={() => setShowModal(false)}>
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="help-modal-content">
                    <div className="help-scroll-wrapper">
                        <div className="help-intro">
                            <IonIcon icon={informationCircleOutline} className="intro-icon" />
                            <IonText color="medium">
                                <p>{helpContent.description}</p>
                            </IonText>
                        </div>

                        <IonList lines="none" className="help-list">
                            {helpContent.sections.map((section, idx) => (
                                <IonItem key={idx} className="help-item">
                                    <div slot="start" className="item-number">{idx + 1}</div>
                                    <IonLabel>
                                        <h2 className="section-title">{section.title}</h2>
                                        <p className="section-detail">{section.detail}</p>
                                    </IonLabel>
                                </IonItem>
                            ))}
                        </IonList>

                        <div className="help-footer">
                            <IonBadge color="primary" mode="ios">Peopoll Assistant v1.0</IonBadge>
                            <IonText color="medium">
                                <small>Tap outside or swipe down to close</small>
                            </IonText>
                        </div>
                    </div>
                </IonContent>
            </IonModal>
        </>
    );
};

export default GlobalHelp;
